'use client';

import { StakeTranslations } from '../../utils/getStakeTranslations';
import { useMemo, type FC } from 'react';
import { useModalDrawerContext } from '../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { FilledArrowRight, RegularStake } from '../../../common/svgIcons';
import { ButtonBase } from '../../../common';
import { useClientLocale } from '@bts-web/utils-lokalise';
import { useRouter } from 'next/navigation';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { useTradeContext } from '../../../trade';
import { StakingAssetSelectDrawer } from '../modalsDrawers/StakingAssetSelectDrawer/StakingAssetSelectDrawer';
import { UnownedAssetDrawer } from '../modalsDrawers/UnownedAssetDrawer/UnownedAssetDrawer';
import { PortfolioStakingOverviewResponse } from '../../../common/gqlActions';
import {
  intlFiatValueFormatting,
  PrecisionFloat,
} from '@bts-web/utils-formatting';
import { getAppConfig } from '@bts-web/core-app-config';
import CarouselCard, {
  btnStyles,
} from '../../../common/components/CarouselCard/CarouselCard';
import { MutableNonNullableAsset } from '../../../trade/CreateTrade/types';

interface StakingCardProps {
  assetData?: Pick<
    MutableNonNullableAsset,
    'symbol' | 'logoUrl' | 'name' | 'id' | 'portfolio'
  >;
  stakingData: PortfolioStakingOverviewResponse | null;
  translations: StakeTranslations;
}

export const StakingCard: FC<StakingCardProps> = ({
  assetData,
  stakingData,
  translations,
}) => {
  const fiatRewardValue = stakingData?.data.overview
    ?.totalRewardsFiat as PrecisionFloat;

  const fiatStakingValue = stakingData?.data.overview
    ?.totalStakedFiat as PrecisionFloat;

  const { currency } = getAppConfig();

  const locale = useClientLocale();

  const totalReward =
    intlFiatValueFormatting(fiatRewardValue?.value || 0, {
      locale,
      currency,
      fractionDigits: fiatRewardValue?.precision,
    }) || null;

  const isEmptyState =
    Number(fiatRewardValue?.value || '0') <= 0 &&
    Number(fiatStakingValue?.value || '0') <= 0;

  const { push } = useRouter();

  const { openDrawer } = useModalDrawerContext();

  const { isPortfolioEmpty } = useTradeContext();

  const assetExistsButNotOwned = useMemo(() => {
    return (
      assetData &&
      Number(assetData.portfolio?.assetBalance?.value ?? 0) > 0 === false
    );
  }, [assetData]);

  const onClickAction = () => {
    if (assetData?.id && !assetExistsButNotOwned) {
      push(`${NEXT_ROUTES.ASSET_DETAILS.replace('[id]', assetData.id)}/stake`);
    } else if (assetData && (isPortfolioEmpty || assetExistsButNotOwned)) {
      openDrawer(UnownedAssetDrawer, {
        assetData: {
          id: assetData.id,
          logoUrl: assetData.logoUrl as string,
          name: assetData.name as string,
        },
        position: 'bottom',
      });
    } else if (!isEmptyState) {
      push(`/staking`);
    } else {
      openDrawer(StakingAssetSelectDrawer);
    }
  };

  const action = (
    <ButtonBase
      className={btnStyles}
      noPadding
      visual="ghost"
      onClick={onClickAction}
    >
      <span>
        {isEmptyState ? translations.stakeNow : translations.checkEarnings}
      </span>
      <FilledArrowRight
        color="positive.fill_primary"
        size={16}
        data-dir="rtl"
      />
    </ButtonBase>
  );

  return (
    <CarouselCard
      title={isEmptyState ? translations.stakeFrameBoxTitle : totalReward}
      subtitle={
        isEmptyState ? translations.noPlanYet : translations.totalRewards
      }
      icon={<RegularStake size={24} />}
      action={action}
      data-testid="savings-plans-frame"
    />
  );
};
